import { useState } from "react";
import { Link } from "react-router-dom";
import { signInWithGoogle, signUpWithPassword } from "@/lib/supabaseAuth";

const normalizeUserType = (type) => {
  if (type === "dueno") return "propietario";
  if (type === "corredor") return "agente";
  return type || "propietario";
};

const GoogleLogo = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.3 9.14 5.38 12 5.38z" />
  </svg>
);

const SignupForm = ({
  defaultUserType = "propietario",
  onSuccess,
  allowSearchUser = false,
  publishPlan = "gratis",
  publishNext,
}) => {
  const normalizedPlan = publishPlan === "pro" ? "pro" : "gratis";
  const nextAfterSignup = publishNext || `/registro?plan=${normalizedPlan}`;
  const googleNext = allowSearchUser ? "/" : nextAfterSignup;
  const googleMode = allowSearchUser ? "general" : "publisher";
  const confirmNext = allowSearchUser ? "/" : nextAfterSignup;
  const [form, setForm] = useState({
    nombre: "",
    telefono: "",
    email: "",
    password: "",
    tipoUsuario: allowSearchUser ? "buscador" : normalizeUserType(defaultUserType),
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFacebookLogin = () => {
    alert("El registro con Facebook estará disponible pronto. Por ahora puedes continuar con Google o correo.");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");
    setMessage("");

    try {
      await signUpWithPassword({
        email: form.email,
        password: form.password,
        redirectTo: `${window.location.origin}/iniciar-sesion?confirmed=1&next=${encodeURIComponent(confirmNext)}`,
        metadata: {
          nombre: form.nombre,
          telefono: `+52 ${form.telefono}`.trim(),
          tipo_usuario: form.tipoUsuario,
          tipo_anunciante: form.tipoUsuario === "buscador" ? "" : form.tipoUsuario,
          interes_buscar: form.tipoUsuario === "buscador",
          interes_publicar: form.tipoUsuario !== "buscador",
        },
      });

      setMessage("Cuenta creada. Revisa tu correo y confirma tu cuenta para iniciar sesión.");
      onSuccess?.();
    } catch (authError) {
      setError(authError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-black text-slate-950">
        {allowSearchUser ? "Crear cuenta" : "Publicar gratis"}
      </h1>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        {allowSearchUser
          ? "Regístrate con tu red social o con correo y contraseña en un solo paso."
          : "Crea tu cuenta como propietario, inmobiliaria o agente para publicar tus fotos y administrar tus propiedades."}
      </p>

      <button
        type="button"
        onClick={() => signInWithGoogle({ next: googleNext, mode: googleMode })}
        className="mt-5 flex w-full items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm font-black text-slate-700 shadow-sm transition hover:bg-slate-50"
      >
        <GoogleLogo />
        Continuar con Google
      </button>

      <button
        type="button"
        onClick={handleFacebookLogin}
        className="mt-3 flex w-full items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm font-black text-slate-700 shadow-sm transition hover:bg-slate-50"
      >
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#1877F2] text-sm font-black text-white">
          f
        </span>
        Continuar con Facebook
      </button>

      <div className="my-4 flex items-center gap-3">
        <span className="h-px flex-1 bg-slate-200" />
        <span className="text-xs font-bold uppercase tracking-wide text-slate-400">
          o con correo
        </span>
        <span className="h-px flex-1 bg-slate-200" />
      </div>

      <form onSubmit={handleSubmit} autoComplete="on" className="space-y-3">
        <input
          required
          type="text"
          name="name"
          autoComplete="name"
          placeholder="Nombre completo"
          value={form.nombre}
          onChange={(event) => updateForm("nombre", event.target.value)}
          className="w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2]"
        />
        <div className="flex overflow-hidden rounded-lg border border-slate-200 bg-white focus-within:border-[#0056D2]">
          <span className="flex items-center border-r border-slate-200 bg-slate-50 px-3 text-sm font-black text-slate-700">
            +52
          </span>
          <input
            required
            type="tel"
            name="tel"
            autoComplete="tel-national"
            placeholder="WhatsApp"
            value={form.telefono}
            onChange={(event) => updateForm("telefono", event.target.value)}
            className="w-full p-3 outline-none"
          />
        </div>
        <input
          required
          type="email"
          name="email"
          autoComplete="username"
          placeholder="Correo electrónico"
          value={form.email}
          onChange={(event) => updateForm("email", event.target.value)}
          className="w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2]"
        />
        <input
          required
          minLength={8}
          type="password"
          name="new-password"
          autoComplete="new-password"
          placeholder="Contraseña, mínimo 8 caracteres"
          value={form.password}
          onChange={(event) => updateForm("password", event.target.value)}
          className="w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2]"
        />
        <select
          value={form.tipoUsuario}
          onChange={(event) => updateForm("tipoUsuario", event.target.value)}
          className="w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2]"
        >
          {allowSearchUser && <option value="buscador">Buscador</option>}
          <option value="propietario">Propietario</option>
          <option value="inmobiliaria">Inmobiliaria</option>
          <option value="agente">Agente</option>
        </select>

        {error && (
          <p className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">
            {error}
          </p>
        )}
        {message && (
          <p className="rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-700">
            {message}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-lg bg-[#F97316] p-3 font-black text-white disabled:bg-orange-300"
        >
          {isSubmitting ? "Creando cuenta..." : "Crear cuenta"}
        </button>
      </form>

      <p className="mt-4 text-center text-sm text-slate-600">
        ¿Ya tienes cuenta?{" "}
        <Link to="/iniciar-sesion" className="font-black text-[#0056D2]">
          Inicia sesión
        </Link>
      </p>

      <p className="mt-5 text-center text-xs leading-5 text-slate-500">
        Al continuar estás aceptando los{" "}
        <Link to="/terminos" className="font-black text-[#0056D2]">
          Términos y condiciones
        </Link>{" "}
        y{" "}
        <Link to="/privacidad" className="font-black text-[#0056D2]">
          Aviso de Privacidad
        </Link>
      </p>
    </div>
  );
};

export default SignupForm;
